import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { ToasterService } from '../modules/shared/services/toaster.service';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest
} from '@angular/common/http';
import { Application } from '../enums/application.enums';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Observable, throwError } from 'rxjs';
import { Messages } from '../enums/messages.enums';
import { catchError, finalize, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
let httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};
let formOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/octet-stream'
  })
};
const baseUrl: string = environment.baseURL;
const resolveUrl = (apiName: string) => (apiName.startsWith('http://') || apiName.startsWith('https://') ? apiName : `${baseUrl}${apiName}`);

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private activeRequestsCount = 0;
  private loaderTimeout: any = null;
  private readonly LOADER_TIMEOUT_DURATION = 10000; // 10 seconds

  constructor(
    private readonly http: HttpClient,
    private readonly ts: ToasterService,
    private readonly authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * @description For handle http request.
   * @param type request type
   * @param apiName API end point
   * @param showLoader loader status
   * @param data  request body
   */

  private showLoaderIfNeeded() {
      if (isPlatformBrowser(this.platformId)) {
        if (this.activeRequestsCount === 0) {
          // Start timeout fallback
          clearTimeout(this.loaderTimeout);
          this.loaderTimeout = setTimeout(() => {
            this.activeRequestsCount = 0;
            console.warn('Loader forcefully hidden due to timeout.');
          }, this.LOADER_TIMEOUT_DURATION);
        }
        this.activeRequestsCount++;
  }

  }
  
  private hideLoaderIfNeeded() {
    this.activeRequestsCount--;
    if (this.activeRequestsCount <= 0) {
      this.activeRequestsCount = 0;
  
      // Clear fallback timeout
      if (this.loaderTimeout) {
        clearTimeout(this.loaderTimeout);
        this.loaderTimeout = null;
      }
    }
  }

  httpRequest(
    type: string,
    apiName: string,
    showLoader: boolean,
    data?: [] | {},
    options?: {},
    responseType?: any // Added responseType
  ): Observable<any> {
    this.setHeader();
    if (type === Application.TYPE_GET) {
      const newOptions: any = {
        ...httpOptions,
        ...(options || {})
      };
      if (responseType) {
        newOptions.responseType = responseType;
      }
      return this.callGetTypeAPI(apiName, showLoader, newOptions);
    }
    if (type === Application.TYPE_POST) {
      const newOptions = {
        ...httpOptions,
        ...(options || {})
      };
      return this.callPostTypeAPI(apiName, data, showLoader, newOptions);
    }
    if (type === Application.TYPE_PUT) {
      return this.callPutTypeAPI(apiName, data, showLoader, httpOptions);
    }
    if (type === Application.TYPE_DELETE) {
      return this.callDeleteTypeAPI(apiName, showLoader, httpOptions);
    }
    if (type === Application.TYPE_PATCH) {
      return this.callPatchTypeAPI(apiName, data, showLoader, httpOptions);
    }
    return null;
  }

  isOnline(): boolean {
    let status = false;
    if (isPlatformBrowser(this.platformId)) {
      if (window?.navigator?.onLine) {
        status = true;
      } else {
        this.ts.showWarning(Messages.INTERNET_CONNECTION_ERROR);
      }
    } else {
      status = true;
    }
    return status;
  }

  callGetTypeAPI(apiName, showLoader, headerOpt: any) {
    if (showLoader) {
      this.showLoaderIfNeeded();
    }
    return this.http.get(resolveUrl(apiName), headerOpt).pipe(
      map((res) => {
        return res;
      }),
      finalize(() => {
        if (showLoader)  {
          this.hideLoaderIfNeeded();
        }
      })
    );
  }

  callPostTypeAPI(
    apiName: string,
    data: any,
    showLoader: boolean,
    headerOpt: any,
    showProgress = false
  ) {
    if (showLoader) {
      this.showLoaderIfNeeded();
    }
    const req = new HttpRequest('POST', resolveUrl(apiName), data, {
      ...headerOpt,
      reportProgress: showProgress
    });
    if (showProgress) {
      return this.http.request(req).pipe(
        map((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              // Calculate and return the upload percentage
              const percentage = Math.round(
                (100 * event.loaded) / (event.total ?? 1)
              );
              return {
                progress: percentage,
                progressStatus: Application.UPLOADING
              };
            case HttpEventType.Response:
              return {
                progress: 100,
                progressStatus: Application.COMPLETED,
                ...event.body
              };
            default:
              return { progress: 0, progressStatus: Application.INITIATED };
          }
        }),
        catchError((error) => {
          return throwError(error);
        }),
        finalize(() => {
          if (showLoader) {
            this.hideLoaderIfNeeded();
          }
        })
      );
    } else {
      return this.http.post(resolveUrl(apiName), data, headerOpt).pipe(
        map((res) => {
          return res;
        }),
        finalize(() => {
          if (showLoader) {
            this.hideLoaderIfNeeded();
          }
        })
      );
    }
  }

  callPutTypeAPI(apiName, data, showLoader, headerOpt) {
    if (showLoader) {
      this.showLoaderIfNeeded();
    }
    return this.http.put(resolveUrl(apiName), data, headerOpt).pipe(
      map((res) => {
        return res;
      }),
      finalize(() => {
        if (showLoader) {
          this.hideLoaderIfNeeded();
        }
      })
    );
  }

  callDeleteTypeAPI(apiName, showLoader, headerOpt) {
    if (showLoader) {
      this.showLoaderIfNeeded();
    }
    return this.http.delete(resolveUrl(apiName), headerOpt).pipe(
      map((res) => {
        return res;
      }),
      finalize(() => {
        if (showLoader) {
          this.hideLoaderIfNeeded();
        }
      })
    );
  }

  callPatchTypeAPI(apiName, data, showLoader, headerOpt) {
    if (showLoader) {
      this.showLoaderIfNeeded();
    }
    return this.http.patch(resolveUrl(apiName), data, headerOpt).pipe(
      map((res) => {
        return res;
      }),
      finalize(() => {
        if (showLoader) {
          this.hideLoaderIfNeeded();
        }
      })
    );
  }

  /**
   * @description Multipart request (form data)
   * @param type  Request type
   * @param apiName  API end point
   * @param showLoader Loader status
   * @param data Request data.
   */
  formRequest(
    type: string,
    apiName: string,
    showLoader: boolean,
    data?: [] | {},
    showProgress = false
  ) {
    this.setFormHeader();
    if (this.isOnline()) {
    if (type === Application.TYPE_GET) {
      return this.callGetTypeAPI(apiName, showLoader, formOptions);
    }
    if (type === Application.TYPE_POST) {
        return this.callPostTypeAPI(
          apiName,
          data,
          showLoader,
          formOptions,
          showProgress
        );
    }
    if (type === Application.TYPE_PUT) {
      return this.callPutTypeAPI(apiName, data, showLoader, formOptions);
    }
    if (type === Application.TYPE_DELETE) {
      return this.callDeleteTypeAPI(apiName, showLoader, formOptions);
    }
    if (type === Application.TYPE_PATCH) {
      return this.callPatchTypeAPI(apiName, data, showLoader, formOptions);
      }
    }
    return null;
  }

  setHeader() {
    if (this.authService.getUserIndexedDb() || this.authService.accessToken.value) {
      const detailObj = this.authService.getUserIndexedDb() || {};
      const primaryUser = this.authService.getDelegateAuthToken() || {};
      httpOptions.headers = httpOptions.headers.set(
        'Authorization',
        String(detailObj.fm_user) || this.authService.accessToken.value
      );
      if (primaryUser?.fm_user) {
        httpOptions.headers = httpOptions.headers.set(
          'Delegate-Authorization',
          String(primaryUser.fm_user)
        );
      }
      httpOptions.headers = httpOptions.headers.set(
        'User-Timezone',
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    } else {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
    }
  }

  setFormHeader() {
    if (this.authService.getUserIndexedDb()) {
      const detailObj = this.authService.getUserIndexedDb();
      formOptions.headers = formOptions.headers.set(
        'Authorization',
        String(detailObj.fm_user)
      );
      formOptions.headers = formOptions.headers.set(
        'User-Timezone',
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    } else {
      formOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/octet-stream'
        })
      };
    }
  }
}
